import type { ICellRendererParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { ICluster, TClusterStatus } from '~/types/cluster'

import AppLogo from '~/components/AppLogo'
import Badge from '~/components/Badge'
import Tooltip from '~/components/Tooltip'
import { CLUSTER_HARDCODED_TAGS, CLUSTER_STATUSES, getBadgeLabel } from '~/const/clusters'
import { useUsersListQuery } from '~/queries/users'

import './styles.scss'

export interface Props {
  description: string | null
  iconUrl: string | null
  name: string | null
  nbApps: number | null
  status: TClusterStatus | null
  tags: string[] | null
  userId: string | null
}

const MAX_TAGS_ITEM = 3

const ClusterCellRenderer = ({ value }: ICellRendererParams<ICluster, Props>): ReactElement => {
  /** Local state */

  const { data: users } = useUsersListQuery()

  if (!value) return <div />

  const { description, iconUrl: icon_url, name, nbApps, status: statusValue, tags, userId } = value

  const status = CLUSTER_STATUSES.find(({ value }) => statusValue === value)

  const assignee = users?.find(({ user_id }) => user_id === userId)?.first_name

  /** Render */

  return (
    <div className="cluster-cell-renderer">
      <div className="cluster-cell-renderer__left">
        <AppLogo appLogoUrl={icon_url || ''} size="small" />
      </div>
      <div className="cluster-cell-renderer__right">
        <div className="cluster-cell-renderer__right__cluster-name" title={name || ''}>
          {name || 'Nameless cluster 🥷'}
        </div>
        {statusValue || nbApps || assignee ? (
          <div className="cluster-cell-renderer__right__cluster-info">
            <div className="cluster-status" style={{ color: status?.color }}>
              {status?.icon ? <img className="cluster-status__icon--image" src={status.icon} /> : <>❔</>}
              <span className="cluster-status__label">{status?.label || statusValue}</span>
            </div>
            {statusValue && nbApps !== null ? '•' : null}
            {nbApps !== null ? (
              <div className="cluster-nb-apps">
                <span className="cluster-nb-apps--emphasized">{nbApps}</span> app{nbApps > 1 ? 's' : ''}
              </div>
            ) : null}
            {nbApps !== null && assignee ? '•' : null}
            {assignee ? <span className="cluster-assignee">{assignee}</span> : null}
          </div>
        ) : null}
        {description ? (
          <span className="cluster-cell-renderer__right__cluster-description" title={description}>
            {description}
          </span>
        ) : null}
        {tags && tags.length > 0 ? (
          <div className="cluster-cell-renderer__right__cluster-tags-container">
            <div className="cluster-tags">
              {tags.slice(0, MAX_TAGS_ITEM).map((el, idx) => (
                <Badge
                  className="cluster-tags__badge"
                  key={idx}
                  label={getBadgeLabel(el, CLUSTER_HARDCODED_TAGS)}
                  value={el}
                />
              ))}
              {tags.length > MAX_TAGS_ITEM ? (
                <Tooltip
                  align={{ points: ['br', 'tr'] }}
                  className="cluster-cell-renderer__right__tags-tooltip"
                  overlay={
                    <>
                      {tags.slice(MAX_TAGS_ITEM).map((el, idx) => (
                        <Badge
                          className="cluster-tags__badge"
                          key={idx}
                          label={getBadgeLabel(el, CLUSTER_HARDCODED_TAGS)}
                          value={el}
                        />
                      ))}
                    </>
                  }
                  placement="right"
                  trigger="hover"
                >
                  <div>
                    <Badge className="cluster-tags__badge" value={`+${tags.length - MAX_TAGS_ITEM}`} />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ClusterCellRenderer
